import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { GuardNavLinks } from 'src/app/user-management/models/nav-link';
import { TeamPermissionsStateService } from 'src/app/user-management/team/services/team-permissions-state.service';
import {
	TeamAction,
	TeamPermissions,
	TeamRole
} from 'src/app/user-management/team/types/team.types';
import { LoPageHeader, LoTab, LoUnderlineTabs } from 'lo-design-system';

/**
 * The settings entries only the account's manager may use, keyed by label.
 *
 * A member is not shown them at all — the billing routes behind them are closed
 * by `billingOwnerGuard`, and an entry that leads somewhere the person is
 * bounced out of is worse than no entry.
 */
const OWNER_ONLY_LABELS: ReadonlySet<string> = new Set(['Billing', 'Plans']);

const ASSISTANT_LABEL = 'Assistant';
// CONTRACT: the assistant-knowledge API (AccountAdmin → brand_admin_user, app/globals/dependencies/roles.py) lets owners and
// admins read and change it; a read_only seat may READ it but gets 403 on Clear. The tab is for those who manage the account.
const ASSISTANT_ROLES: ReadonlySet<TeamRole | null> = new Set([
	TeamRole.OWNER,
	TeamRole.ADMIN
]);

@Component({
	standalone: true,
	selector: 'app-user-header',
	template: `
		<lo-page-header title="Settings" />
		<lo-underline-tabs
			label="Settings sections"
			[tabs]="tabs"
			[selected]="activeLink ?? ''"
			(selectedChange)="navigate($event)"
		/>
	`,
	imports: [LoPageHeader, LoUnderlineTabs]
})
export class UserHeaderComponent implements OnInit {
	private readonly allNavLinks: GuardNavLinks[] = [
		{
			index: 0,
			label: 'My Profile',
			link: '/user-management/my-profile',
			disable: false
		},
		{
			// The team surface has existed at this address since before this
			// strip did and had no entry pointing at it, so the only way in was
			// to type the URL. Second, next to the profile: both are about who
			// is on the account rather than what it pays for.
			index: 1,
			label: 'Team',
			link: '/user-management/teams',
			disable: false
		},
		{
			index: 3,
			label: 'Integrations',
			link: '/user-management/integrations',
			disable: false
		},
		{
			index: 4,
			label: 'Billing',
			link: '/user-management/billing',
			disable: false
		},
		{
			index: 5,
			label: 'Credits',
			link: '/user-management/credits',
			disable: false
		},
		{
			index: 6,
			label: 'Plans',
			link: '/user-management/manage-plan',
			disable: false
		},
		{
			index: 7,
			label: 'Assistant',
			link: '/user-management/assistant-knowledge',
			disable: false
		}
	];
	/**
	 * What the strip draws. Starts as the WHOLE set and is narrowed once the
	 * role matrix answers — the same fail-open the guard uses, for the same
	 * reason: an owner whose matrix read failed must not lose their own billing
	 * entry, and the route behind it refuses a member either way.
	 */
	public navLinks: GuardNavLinks[] = this.allNavLinks;
	/** The strip's tabs, rebuilt whenever navLinks is narrowed. The label is the tab id. */
	public tabs: LoTab[] = this.toTabs(this.allNavLinks);
	public activeLink: string | null = this.allNavLinks[0].label;

	private unsubscriber$: Subject<void> = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private permissionsState: TeamPermissionsStateService
	) {}

	ngOnInit() {
		this.initPathListeners();
		this.applyRole();
	}

	private applyRole(): void {
		this.permissionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(permissions => {
				const managesBilling = this.permissionsState.can(
					TeamAction.BILLING_MANAGE
				);
				const readsAssistant = this.assistantAllowed(permissions);
				if (managesBilling && readsAssistant) {
					return;
				}
				this.navLinks = this.allNavLinks.filter(
					nav =>
						(managesBilling || !OWNER_ONLY_LABELS.has(nav.label)) &&
						(readsAssistant || nav.label !== ASSISTANT_LABEL)
				);
				this.tabs = this.toTabs(this.navLinks);
			});
	}

	/** Fail-open on a matrix that could not be read, like the billing entries. */
	private assistantAllowed(permissions: TeamPermissions | null): boolean {
		return !permissions || ASSISTANT_ROLES.has(permissions.role);
	}

	initPathListeners() {
		const updateActiveLink = (url: string) => {
			this.activeLink = null;
			this.navLinks.forEach(nav => {
				if (url.includes(nav.link)) {
					this.activeLink = nav.label;
				}
			});
		};

		updateActiveLink(this.router.url);

		this.router.events
			.pipe(
				filter(
					(event): event is NavigationEnd =>
						event instanceof NavigationEnd
				),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(event => {
				updateActiveLink(event.urlAfterRedirects);
			});
	}

	/** (selectedChange) of the strip: the tab id is the nav label. */
	public navigate(label: string): void {
		const nav = this.navLinks.find(link => link.label === label);
		// TRAP: lo-underline-tabs echoes (selectedChange) with its first tab when [selected] names no tab
		// (a route that matches no entry) — that echo is not a click, and must not navigate.
		const stripHasSelection = this.navLinks.some(
			link => link.label === this.activeLink
		);
		const isEcho =
			label === this.activeLink ||
			(!stripHasSelection && label === this.navLinks[0]?.label);
		if (!nav || isEcho) {
			return;
		}
		this.router.navigateByUrl(nav.link);
	}

	private toTabs(links: GuardNavLinks[]): LoTab[] {
		return links.map(nav => ({ id: nav.label, label: nav.label }));
	}

	ngOnDestroy() {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}
}

export default UserHeaderComponent;
